// Command palette (UX spec 6.1), opened with Ctrl/Cmd+K anywhere. It offers the nav's pages, the
// teammates you can call, calls in the ledger and the everyday actions. Keyboard first: type to
// filter, Up and Down to move, Enter to run, Esc to close. It is a modal <dialog>, so the page behind
// is inert, Tab stays in the field and focus goes back where it was; the result count is announced
// inside the dialog as you type. Calls are only ever prefilled in the dialer: you still press Call.
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { CircleDot, History, Keyboard, type LucideIcon, Moon, Phone, PhoneOutgoing, Rows3, Search, Settings, Sun, SunMoon } from 'lucide-react';
import type { Agent } from '@bcio-voice/contracts';
import { Icon } from '../ui/Icon.tsx';
import { Keys } from '../ui/Keys.tsx';
import { useAnnouncer } from '../ui/Announcer.tsx';
import { useRouter } from '../lib/router.tsx';
import { useWorkspace } from '../state/workspace.tsx';
import { useDensity } from '../state/density.ts';
import { capability, gate, hasPermission } from '../lib/capabilities.ts';
import { pstnReadiness } from '../call/pstn.ts';
import { isIncomingFor } from '../call/callStore.ts';
import { formatRelative, parsePhoneInput } from '../lib/format.ts';
import { effectiveTheme, getThemePreference, setThemePreference, subscribeTheme } from '../lib/theme.ts';
import { type PaletteIcon, type PaletteItem, type PaletteSource, flatItems, moveActive, paletteGroups, resultsAnnouncement } from '../lib/palette.ts';
import { detectPlatform, findShortcut, shortcutText, singleKeyShortcutsOn, subscribeSingleKey } from '../lib/shortcuts.ts';
import { directionLabel, outcomeLabel, partyName } from '../features/phone/callView.ts';
import { presenceText } from '../features/phone/PhoneDock.tsx';
import { useNav } from './nav.tsx';
import { AVAILABILITY } from './availability.ts';

const ICONS: Record<Exclude<PaletteIcon, 'page'>, LucideIcon> = {
  phone: Phone,
  dial: PhoneOutgoing,
  call: History,
  status: CircleDot,
  'theme-day': Sun,
  'theme-night': Moon,
  'theme-system': SunMoon,
  density: Rows3,
  keyboard: Keyboard,
};

/** Digits and phone punctuation only, with at least three digits: offer to dial it. */
const PHONE_LIKE = /^\+?[\d\s().-]+$/;

export function CommandPalette({ open, onClose, onShowShortcuts }: { open: boolean; onClose: () => void; onShowShortcuts: () => void }) {
  const ws = useWorkspace();
  const router = useRouter();
  const nav = useNav();
  const { announce } = useAnnouncer();
  const [density, setDensity] = useDensity();
  const theme = useSyncExternalStore(subscribeTheme, getThemePreference, () => 'system' as const);
  const platform = useMemo(() => detectPlatform(), []);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [spoken, setSpoken] = useState('');
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const returnTo = useRef<Element | null>(null);
  const restoreFocus = useRef(true);
  const baseId = useId();
  const listId = `${baseId}-list`;
  const hintId = `${baseId}-hint`;

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      returnTo.current = document.activeElement;
      restoreFocus.current = true;
      setQuery('');
      setActive(0);
      el.showModal();
      inputRef.current?.focus();
    } else if (!open && el.open) {
      el.close();
      if (restoreFocus.current && returnTo.current instanceof HTMLElement) returnTo.current.focus();
    }
  }, [open]);

  const singleKey = useSyncExternalStore(subscribeSingleKey, singleKeyShortcutsOn, () => true);
  const source = usePaletteSource(nav, query, density, theme, singleKey ? platform : null);
  const groups = open ? paletteGroups(query, source) : [];
  const items = flatItems(groups);
  const current = items[Math.min(active, items.length - 1)] ?? null;
  const optionId = (item: PaletteItem) => `${baseId}-opt-${item.id.replace(/[^\w-]/g, '_')}`;

  // The count is spoken once typing pauses, not on every key.
  const announcement = open ? resultsAnnouncement(items.length, query) : '';
  useEffect(() => {
    if (!open) { setSpoken(''); return; }
    const t = setTimeout(() => setSpoken(announcement), 400);
    return () => clearTimeout(t);
  }, [open, announcement]);

  // Keep the active row in view as the arrows move it.
  useEffect(() => {
    if (current) document.getElementById(optionId(current))?.scrollIntoView?.({ block: 'nearest' });
  }, [current?.id]);

  const run = (item: PaletteItem) => {
    const command = item.command;
    // Commands that put focus somewhere new keep it there; the rest return it where it was.
    restoreFocus.current = command.kind === 'set_status' || command.kind === 'set_theme' || command.kind === 'set_density';
    onClose();
    switch (command.kind) {
      case 'navigate':
        router.navigate(command.to);
        break;
      case 'call_teammate':
      case 'dial':
        ws.requestDial(command.kind === 'dial' ? { kind: 'pstn', input: command.input, fromNumberId: null } : { kind: 'internal', userId: command.userId });
        // Below the docked width the phone is its own page.
        if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 1023px)').matches && router.route.name !== 'phone') router.navigate('/phone');
        break;
      case 'set_status': {
        const label = AVAILABILITY.find(a => a.value === command.status)?.label ?? command.status;
        void ws.setAvailability(command.status as Agent['availability']).then(error => announce(error ? error.title : `Status set to ${label}`));
        break;
      }
      case 'set_theme':
        setThemePreference(command.theme);
        announce(command.theme === 'system' ? 'Theme matches the system' : `${command.theme === 'night' ? 'Night' : 'Day'} theme on`);
        break;
      case 'set_density':
        setDensity(command.density);
        announce(command.density === 'compact' ? 'Compact density on' : 'Comfortable density on');
        break;
      case 'show_shortcuts':
        // After the palette has handed focus back, so the help returns it there too.
        restoreFocus.current = true;
        setTimeout(onShowShortcuts, 0);
        break;
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === 'Tab') {
      // The field is the one stop in the palette: Tab and Shift+Tab stay on it.
      event.preventDefault();
      inputRef.current?.focus();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === 'Enter') {
      if (event.nativeEvent.isComposing) return;
      event.preventDefault();
      if (current) run(current);
      return;
    }
    const next = moveActive(event.key, Math.min(active, items.length - 1), items.length);
    if (next === null) return;
    event.preventDefault();
    setActive(next);
  };

  const pageIcons = new Map<string, LucideIcon>(nav.map(item => [item.to, item.icon]));
  return (
    <dialog
      ref={dialogRef}
      className="palette"
      aria-label="Command palette"
      onCancel={event => { event.preventDefault(); onClose(); }}
      onKeyDown={onKeyDown}
      onClick={event => { if (event.target === dialogRef.current) onClose(); }}
    >
      {open ? (
        <>
          <div className="palette-field">
            <Icon icon={Search} />
            <input
              ref={inputRef}
              className="palette-input"
              type="text"
              role="combobox"
              aria-label="Search pages, teammates, calls and actions"
              aria-expanded={items.length > 0}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={current ? optionId(current) : undefined}
              aria-describedby={hintId}
              placeholder="Pages, people, calls or actions"
              autoComplete="off"
              spellCheck={false}
              value={query}
              onChange={event => { setQuery(event.target.value); setActive(0); }}
            />
          </div>
          <div id={listId} className="palette-results" role="listbox" aria-label="Results">
            {groups.map(group => (
              <div key={group.id} role="group" aria-labelledby={`${baseId}-${group.id}`}>
                <div id={`${baseId}-${group.id}`} className="palette-group-head" role="presentation">{group.title}</div>
                {group.items.map(item => {
                  const index = items.indexOf(item);
                  const selected = item === current;
                  const icon = item.icon === 'page' ? (pageIcons.get(item.command.kind === 'navigate' ? item.command.to : '') ?? Settings) : ICONS[item.icon];
                  return (
                    <div
                      key={item.id}
                      id={optionId(item)}
                      role="option"
                      aria-selected={selected}
                      className={`palette-option${selected ? ' is-active' : ''}`}
                      onMouseDown={event => event.preventDefault()}
                      onMouseMove={() => { if (!selected) setActive(index); }}
                      onClick={() => run(item)}
                    >
                      <Icon icon={icon} />
                      <span className="palette-option-text">
                        <span className="palette-option-label">{item.label}</span>
                        {item.detail ? <span className="palette-option-detail">{item.detail}</span> : null}
                      </span>
                      {item.hint ? <span className="palette-option-hint">{item.hint}</span> : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          {items.length === 0 ? <p className="palette-empty">Nothing matches “{query.trim()}”. Try a page, a teammate’s name, a phone number or an action such as “theme”.</p> : null}
          <div id={hintId} className="palette-foot">
            <span><Keys keys={[['Up'], ['Down']]} platform={platform} /> to move</span>
            <span><Keys keys={[['Enter']]} platform={platform} /> to choose</span>
            <span><Keys keys={[['Esc']]} platform={platform} /> to close</span>
          </div>
          <p className="visually-hidden" aria-live="polite" aria-atomic="true">{spoken}</p>
        </>
      ) : null}
    </dialog>
  );
}

/** The workspace as palette input: pages, people, calls and the state the actions toggle. The help
 * hint is left off when single-key shortcuts are turned off. */
function usePaletteSource(nav: ReturnType<typeof useNav>, query: string, density: PaletteSource['density'], themePreference: PaletteSource['theme']['preference'], helpPlatform: ReturnType<typeof detectPlatform> | null): PaletteSource {
  const ws = useWorkspace();
  const q = query.trim();
  const inCall = ws.liveCalls.some(c => !isIncomingFor(c, ws.userId));
  const internal = gate(capability(ws.config.value, 'calling.internal'));
  const pstnCap = capability(ws.config.value, 'calling.pstn.outbound');
  const pstn = pstnReadiness(pstnCap);
  const agents = new Map((ws.agents.value ?? []).map(a => [a.user_id, a]));
  const digitCount = q.replace(/\D/g, '').length;
  const parsed = PHONE_LIKE.test(q) && digitCount >= 3 ? parsePhoneInput(q) : null;
  const help = findShortcut('help');
  return {
    pages: [...nav.map(item => ({ to: item.to, label: item.label })), { to: '/settings', label: 'Settings' }],
    teammates: (ws.roster.value ?? [])
      .filter(u => u.id !== ws.userId && u.active)
      .map(u => {
        const agent = agents.get(u.id);
        return { id: u.id, name: u.display_name, ...(agent ? { presence: presenceText(agent.availability, agent.device_online) } : {}) };
      }),
    calls: q
      ? ws.history.ids.flatMap(id => {
        const call = ws.calls.get(id);
        if (!call) return [];
        const numbers = [call.from.e164, call.destination.kind === 'pstn' ? call.destination.e164 : null].filter((n): n is string => !!n);
        return [{ id: call.id, party: partyName(call, ws.userId, ws.userName), detail: `${directionLabel(call, ws.userId)}, ${outcomeLabel(call).toLowerCase()}, ${formatRelative(call.created_at)}`, numbers }];
      })
      : [],
    canCallTeammates: !inCall && internal.render !== 'hidden' && ws.roster.value !== null,
    dial: { offered: !inCall && gate(pstnCap).render !== 'hidden', blockedReason: pstn.ready ? null : pstn.reason },
    typedNumber: parsed ? { input: q, display: parsed.ok ? parsed.display : q } : null,
    status: ws.me && hasPermission(ws.permissions, 'presence:write_own') ? { current: ws.me.availability, options: AVAILABILITY } : null,
    theme: { preference: themePreference, showing: effectiveTheme(themePreference) },
    density,
    helpHint: help && helpPlatform ? shortcutText(help.keys, helpPlatform) : null,
  };
}
